"use client"

import { useMemo } from "react"
import { useParams } from "@tanstack/react-router"
import { format } from "date-fns"
import { Check, Loader2, TriangleAlert, X } from "lucide-react"

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  FieldShell,
  FIELD_LABEL,
  FIELD_MONO,
  FIELD_UNDERLINE,
  Segmented,
} from "@/components/graphite/field"
import { useIsMobile } from "@/hooks/use-mobile"
import { useCalendarEventDialogController } from "@/hooks/use-calendar-event-dialog-controller"
import { createCalendarEventFormDefaults } from "@/lib/calendar-event-defaults"
import { firstClearStart, readClashes, shiftedEnd } from "@/lib/calendar-clash"
import { stageHex } from "@/lib/stage-colors"
import { cn } from "@/lib/utils"
import type { CalendarEventFormDataV2 } from "@/types/calendar"
import { useGetJobsJobId, useGetJobsJobIdCandidate } from "openapi/api/endpoints"

/**
 * Booking the interview a pipeline drop implies, from canvas artboards 15c
 * (wide) and 15d (narrow), with 15g/15h as the light pair.
 *
 * "A drop into interviewing means a date, so the event is prefilled from the
 * record — title, type, link — and the only real work is the time. The clash
 * check is the reason this screen exists: it names the conflicting event and
 * offers the first clear slot as one tap, rather than making you open the
 * calendar."
 *
 * This is the pipeline's own booking surface, not the general calendar dialog:
 * every picker that dialog carries — which job, which candidate, which link —
 * was already answered by the drag.
 */

const EVENT_TYPES = ["Interview", "Screening", "Meeting", "Other"] as const
type EventTypeLabel = (typeof EVENT_TYPES)[number]
type EventTypeValue = CalendarEventFormDataV2["event_type"]

const asLabel = (value: EventTypeValue): EventTypeLabel =>
  ((value[0]?.toUpperCase() ?? "") + value.slice(1)) as EventTypeLabel
const asValue = (label: EventTypeLabel) => label.toLowerCase() as EventTypeValue

type PipelineStageEventDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  candidateId?: string
  stage: "interviewing" | "screening"
}

export function PipelineStageEventDialog({
  open,
  onOpenChange,
  candidateId,
  stage,
}: PipelineStageEventDialogProps) {
  const params = useParams({ strict: false }) as { jobId?: string }
  const jobId = params.jobId ?? ""
  const isMobile = useIsMobile()

  const { data: job } = useGetJobsJobId(jobId, { query: { enabled: !!jobId && open } })
  const { data: candidateProfile } = useGetJobsJobIdCandidate(
    jobId,
    { candidate_id: candidateId ?? "" },
    { query: { enabled: !!jobId && !!candidateId && open } },
  )
  const candidateName = candidateProfile?.full_name ?? "Candidate"

  const defaultValues = useMemo<Partial<CalendarEventFormDataV2>>(() => {
    const jobTitle = job?.title ?? null
    const eventType = stage === "screening" ? "screening" : "interview"
    const stageLabel = stage === "screening" ? "Screening" : "Interview"
    const title = candidateId
      ? jobTitle
        ? `${candidateName} @${jobTitle}`
        : `${candidateName} - ${stageLabel}`
      : jobTitle
        ? `${stageLabel} @${jobTitle}`
        : stageLabel

    return createCalendarEventFormDefaults({
      title,
      event_type: eventType,
      linkType: candidateId ? "candidate" : "job",
      job_id: jobId,
      candidate_id: candidateId ?? "",
    })
  }, [candidateId, candidateName, job?.title, jobId, stage])

  const controller = useCalendarEventDialogController({
    open,
    onOpenChange,
    defaultValues,
    mode: "create",
  })

  const body = (
    <EventForm
      controller={controller}
      candidateName={candidateName}
      jobTitle={job?.title ?? undefined}
      companyName={job?.company_name ?? undefined}
      stage={stage}
      compact={isMobile}
      onCancel={() => onOpenChange(false)}
    />
  )

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          hideClose
          className="flex h-[92vh] flex-col gap-0 rounded-t-2xl p-0"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <div className="flex flex-none items-center gap-2 border-b px-2 pb-3">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="inline-flex h-11 items-center px-3 text-[13px] text-muted-foreground"
            >
              Cancel
            </button>
            <SheetTitle className="min-w-0 flex-1 truncate text-center text-[14.5px] font-semibold">
              {stage === "screening" ? "Screening" : "Interview"}
            </SheetTitle>
            <button
              type="button"
              onClick={controller.handleSubmit(controller.handleFormSubmit)}
              disabled={controller.isSubmitting}
              className="inline-flex h-11 items-center px-3 text-[13px] font-medium text-brand-ink disabled:opacity-40"
            >
              {controller.isSubmitting ? "Booking…" : "Book"}
            </button>
          </div>
          {body}
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onOpenAutoFocus={(event) => event.preventDefault()}
        className="flex max-h-[88vh] w-[calc(100vw-2rem)] max-w-[620px] flex-col gap-0 overflow-hidden p-0"
      >
        <DialogTitle className="sr-only">
          Book the {stage === "screening" ? "screening" : "interview"}
        </DialogTitle>
        {body}
      </DialogContent>
    </Dialog>
  )
}

type Controller = ReturnType<typeof useCalendarEventDialogController>

function EventForm({
  controller,
  candidateName,
  jobTitle,
  companyName,
  stage,
  compact,
  onCancel,
}: {
  controller: Controller
  candidateName: string
  jobTitle?: string
  companyName?: string
  stage: "interviewing" | "screening"
  compact: boolean
  onCancel: () => void
}) {
  const {
    register,
    setValue,
    handleSubmit,
    handleFormSubmit,
    selectedDate,
    startTime,
    endTime,
    eventType,
    datePickerOpen,
    setDatePickerOpen,
    isSubmitting,
    isBusySlotsPending,
    overlappingBusySlots,
    sendInvites,
    inviteeEmails,
    inviteeDraft,
    setInviteeDraft,
    handleInviteeDraftKeyDown,
    handleInviteeDraftBlur,
    removeInviteeEmail,
    handleStartTimeChange,
    handleEndTimeChange,
    includeInviteMessage,
    supportsInviteMessage,
    allowAttachments,
    newAttachments,
  } = controller

  const clashes = useMemo(() => readClashes(overlappingBusySlots), [overlappingBusySlots])
  const clearStart = firstClearStart(clashes)
  const pad = compact ? "px-[18px]" : "px-[26px]"
  const noun = stage === "screening" ? "screening" : "interview"

  const moveToClearSlot = () => {
    if (!clearStart) return
    const nextEnd = shiftedEnd(startTime, endTime, clearStart)
    handleStartTimeChange(clearStart)
    handleEndTimeChange(nextEnd)
  }

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className="flex min-h-0 flex-1 flex-col"
    >
      <header className={cn("flex-none border-b pb-[14px]", pad, compact ? "pt-3.5" : "pt-5")}>
        {!compact && (
          <div className="text-[16px] font-semibold leading-[1.2] tracking-[-0.02em]">
            Book the {noun}
          </div>
        )}
        <p
          className={cn(
            "text-[12.5px] leading-[1.5] text-muted-foreground",
            !compact && "mt-2",
          )}
        >
          {candidateName} moved to <span style={{ color: stageHex(stage) }}>{stage}</span>
          {jobTitle ? ` · ${jobTitle}` : ""}
          {companyName && !compact ? ` @ ${companyName}` : ""}
        </p>
      </header>

      <fieldset
        disabled={isSubmitting}
        className={cn("min-h-0 flex-1 overflow-y-auto pt-5", pad, compact ? "pb-10" : "pb-6")}
      >
        <FieldShell label="Event title">
          <input
            {...register("title", { required: true })}
            aria-label="Event title"
            className={cn(FIELD_UNDERLINE, "pb-[7px] text-[12.5px] leading-[1.4]")}
          />
        </FieldShell>

        <Segmented
          label="Event type"
          className="mt-5"
          options={EVENT_TYPES}
          value={asLabel(eventType)}
          onChange={(label) => setValue("event_type", asValue(label))}
        />

        <div className={cn("mt-5 flex gap-5", compact && "gap-[18px]")}>
          <FieldShell label="Date" className="flex-[1.4]">
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label={selectedDate ? `Date, ${format(selectedDate, "EEEE d MMMM yyyy")}` : "Date"}
                  className={cn(
                    FIELD_UNDERLINE,
                    FIELD_MONO,
                    "pb-[7px] text-left",
                    !selectedDate && "text-muted-foreground",
                  )}
                >
                  {selectedDate ? format(selectedDate, "EEE d MMM yyyy") : "—"}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => {
                    setValue("date", date as Date)
                    setDatePickerOpen(false)
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </FieldShell>

          <FieldShell label="Start" className="w-[86px] flex-none">
            <input
              type="time"
              value={startTime}
              onChange={(event) => handleStartTimeChange(event.target.value)}
              aria-label="Start time"
              className={cn(FIELD_UNDERLINE, FIELD_MONO, "pb-[7px]")}
            />
          </FieldShell>

          <FieldShell label="End" className="w-[86px] flex-none">
            <input
              type="time"
              value={endTime}
              onChange={(event) => handleEndTimeChange(event.target.value)}
              aria-label="End time"
              className={cn(FIELD_UNDERLINE, FIELD_MONO, "pb-[7px]")}
            />
          </FieldShell>
        </div>

        {isBusySlotsPending && (
          <p className="mt-3.5 font-mono text-[11px] text-muted-foreground">
            Checking the calendar…
          </p>
        )}

        {!isBusySlotsPending && clashes.length > 0 && (
          <ClashBand clashes={clashes} clearStart={clearStart} onMove={moveToClearSlot} />
        )}

        <FieldShell label="Location" className="mt-5">
          <input
            {...register("location")}
            placeholder="Google Meet — link on save"
            aria-label="Location"
            className={cn(FIELD_UNDERLINE, "pb-[7px] text-[12.5px] leading-[1.4]")}
          />
        </FieldShell>

        <FieldShell label="Notes" className="mt-5">
          <textarea
            {...register("notes")}
            rows={3}
            placeholder="What this round is for, and who is on the panel…"
            aria-label="Notes"
            className={cn(FIELD_UNDERLINE, "resize-none pb-2.5 text-[12.5px] leading-[1.7]")}
          />
        </FieldShell>

        <section className="mt-[22px]">
          <div className="flex items-baseline gap-3.5">
            <div className={FIELD_LABEL}>Invites</div>
            <button
              type="button"
              onClick={() => setValue("send_invites", !sendInvites)}
              className={cn(
                "ml-auto font-mono text-[11px] transition-colors",
                sendInvites ? "text-brand-ink" : "text-muted-foreground",
              )}
            >
              {sendInvites ? "sending on save" : "not sending"}
            </button>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2.5 border-b pb-[9px]">
            {inviteeEmails.map((email) => (
              <span
                key={email}
                className="inline-flex items-center gap-[7px] text-[12px] text-foreground"
              >
                {email}
                <button
                  type="button"
                  onClick={() => removeInviteeEmail(email)}
                  aria-label={`Remove ${email}`}
                  className="text-muted-foreground transition-colors hover:text-destructive"
                >
                  <X className="h-[11px] w-[11px]" />
                </button>
              </span>
            ))}
            <input
              value={inviteeDraft}
              onChange={(event) => setInviteeDraft(event.target.value)}
              onKeyDown={handleInviteeDraftKeyDown}
              onBlur={handleInviteeDraftBlur}
              placeholder="add…"
              aria-label="Add an invitee"
              className="min-w-[120px] flex-1 bg-transparent text-[12px] outline-none placeholder:text-muted-foreground"
            />
          </div>

          {/*
            15c draws invites as a bare list of addresses, but the dialog it
            replaces also carried a custom invite message and attachments. The
            artboard not showing them is not a decision to drop them, so they
            stay — quieter, and only where the connected provider allows.
          */}
          {supportsInviteMessage ? (
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setValue("include_invite_message", !includeInviteMessage)}
                className={cn(
                  "text-[11.5px] transition-colors",
                  includeInviteMessage
                    ? "text-brand-ink"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {includeInviteMessage ? "Message included" : "Add a message"}
              </button>
              {includeInviteMessage && (
                <textarea
                  {...register("invite_message")}
                  rows={3}
                  placeholder="A note for the invitees…"
                  aria-label="Invite message"
                  className={cn(
                    FIELD_UNDERLINE,
                    "mt-2 resize-none pb-2.5 text-[12.5px] leading-[1.7]",
                  )}
                />
              )}
            </div>
          ) : (
            <p className="mt-3 text-[11px] text-muted-foreground">
              The connected calendar provider does not support a custom invite
              message.
            </p>
          )}

          {allowAttachments && (
            <div className="mt-4">
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                {newAttachments.map((file, index) => (
                  <span
                    key={`${file.name}-${index}`}
                    className="inline-flex items-center gap-[7px] text-[12px] text-foreground"
                  >
                    {file.name}
                    <button
                      type="button"
                      onClick={() =>
                        setValue(
                          "attachments",
                          newAttachments.filter((_, at) => at !== index),
                          { shouldDirty: true },
                        )
                      }
                      aria-label={`Remove ${file.name}`}
                      className="text-muted-foreground transition-colors hover:text-destructive"
                    >
                      <X className="h-[11px] w-[11px]" />
                    </button>
                  </span>
                ))}
              </div>
              <label className="mt-2 inline-block cursor-pointer text-[11.5px] text-muted-foreground transition-colors hover:text-foreground">
                Attach a file
                <input
                  type="file"
                  multiple
                  aria-label="Attach files"
                  onChange={(event) => {
                    const files = Array.from(event.target.files ?? [])
                    setValue("attachments", [...newAttachments, ...files], {
                      shouldDirty: true,
                    })
                    event.target.value = ""
                  }}
                  className="sr-only"
                />
              </label>
            </div>
          )}
        </section>
      </fieldset>

      {compact ? (
        <div className="flex-none border-t px-[18px] py-3">
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-primary/[0.08] text-[13px] font-medium text-brand-ink disabled:opacity-40"
          >
            {isSubmitting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
            {sendInvites ? "Book and send invites" : "Book"}
          </button>
        </div>
      ) : (
        <div className="flex flex-none items-center gap-[22px] border-t px-[26px] pb-5 pt-[18px]">
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 text-[12.5px] font-medium text-brand-ink transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isSubmitting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
            {sendInvites ? "Book and send invites" : "Book"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="text-[12.5px] text-muted-foreground transition-colors hover:text-foreground"
          >
            Cancel
          </button>
          <span className="ml-auto font-mono text-[11px] text-muted-foreground">
            already at {stage}
          </span>
        </div>
      )}
    </form>
  )
}

/**
 * "The clash sits between the times and the location as a bordered band, so it
 * cannot be scrolled past without being read."
 */
function ClashBand({
  clashes,
  clearStart,
  onMove,
}: {
  clashes: ReturnType<typeof readClashes>
  clearStart?: string
  onMove: () => void
}) {
  return (
    <div className="mt-[18px]">
      <div className="flex items-baseline gap-2.5">
        <TriangleAlert className="h-[13px] w-[13px] flex-none translate-y-[2px] text-destructive" />
        <span className="text-[11.5px] font-medium text-destructive">
          Clashes with {clashes.length} event{clashes.length === 1 ? "" : "s"}
        </span>
        {clearStart && (
          <button
            type="button"
            onClick={onMove}
            className="ml-auto text-[11.5px] font-medium text-brand-ink transition-opacity hover:opacity-80"
          >
            Move to {clearStart}
          </button>
        )}
      </div>

      <div className="mt-2.5">
        {clashes.map((clash, index) => (
          <div
            key={`${clash.when}-${index}`}
            className="flex items-baseline gap-3 border-t py-[9px]"
          >
            <span className="h-[5px] w-[5px] flex-none self-center rounded-full bg-destructive" />
            <span className="w-[104px] flex-none font-mono text-[11px] text-destructive">
              {clash.when}
            </span>
            <span className="min-w-0 flex-1 truncate text-[12px] text-muted-foreground">
              {clash.name}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
